import React, {useCallback, useState} from 'react';
import {searchDevice, unpairDevice} from '../../api/adminAPI';
import {Device} from '../../interfaces';
import {Box, Button, Typography, Alert, Paper, Grid} from '@mui/material';
import PageTitle from '../shared/PageTitle';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormTextField from '../shared/FormTextField';
import PairDeviceModal from './PairDeviceModal';

interface Props {}

type FormValues = {
  qrCode: string;
  deviceId: string;
};

const AdminDevices: React.FC<Props> = () => {
  const {handleSubmit, reset, control} = useForm<FormValues>();
  const {t} = useTranslation();

  const [device, setDevice] = useState<Device | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unpairing, setUnpairing] = useState(false);
  const [unpairSuccess, setUnpairSuccess] = useState<string | null>(null);
  const [pairSuccess, setPairSuccess] = useState<string | null>(null);

  const handleLookupSubmit = useCallback(
    async (data: FormValues) => {
      setPending(true);
      setError(null);
      setDevice(null);
      setUnpairSuccess(null);
      setPairSuccess(null);

      // Validate that at least one field is provided
      if (!data.qrCode && !data.deviceId) {
        setError(t('admin.devices.validationError'));
        setPending(false);
        return;
      }

      try {
        const foundDevice = await searchDevice({
          qrCode: data.qrCode,
          deviceId: data.deviceId ? parseInt(data.deviceId) : undefined,
        });
        setDevice(foundDevice);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('admin.devices.deviceNotFound'),
        );
      } finally {
        setPending(false);
      }
    },
    [t],
  );

  const handleReset = useCallback(() => {
    reset();
    setPending(false);
    setError(null);
    setDevice(null);
    setUnpairSuccess(null);
    setPairSuccess(null);
  }, [reset]);

  const handleUnpairDevice = useCallback(
    async (resetTrial = false) => {
      if (!device || !device.id) return;

      setUnpairing(true);
      setError(null);
      setUnpairSuccess(null);
      setPairSuccess(null);

      try {
        const result = await unpairDevice({
          deviceId: device.id,
          ...(resetTrial && {resetTrialStarted: true}),
        });

        if (result.success) {
          // Refresh the device data to show updated state
          const updatedDevice = await searchDevice({
            qrCode: device.qrCode,
            deviceId: device.id,
          });
          setDevice(updatedDevice);
          setUnpairSuccess(
            resetTrial
              ? t('admin.devices.unpairAndResetSuccess')
              : t('admin.devices.unpairSuccess'),
          );
        } else {
          setError(result.error || t('admin.devices.unpairError'));
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('admin.devices.unpairError'),
        );
      } finally {
        setUnpairing(false);
      }
    },
    [device, t],
  );

  const handlePaired = useCallback(async () => {
    if (!device || !device.id) return;

    setError(null);
    setUnpairSuccess(null);
    setPairSuccess(null);

    try {
      // Refresh the device data to show updated state
      const updatedDevice = await searchDevice({
        qrCode: device.qrCode,
        deviceId: device.id,
      });
      setDevice(updatedDevice);
      setPairSuccess(t('admin.devices.pairSuccess'));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('admin.devices.deviceNotFound'),
      );
    }
  }, [device, t]);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {pageName: t('pages.devices')})}
        </title>
      </Helmet>
      <Box>
        <PageTitle>{t('pages.devices')}</PageTitle>
        <Paper sx={{p: 3, mb: 2}}>
          <Typography variant="h5" sx={{mb: 2}}>
            {t('admin.devices.lookupTitle')}
          </Typography>
          <form onSubmit={handleSubmit(handleLookupSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="qrCode"
              label={t('admin.devices.qrCode') as string}
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="deviceId"
              label={t('admin.devices.deviceId') as string}
              numbersOnly
            />
            <Box sx={{mt: 2, display: 'flex', gap: 2}}>
              <Button disabled={pending} type="submit" variant="contained">
                {pending
                  ? t('admin.devices.lookingUp')
                  : t('admin.devices.lookupButton')}
              </Button>
              <Button onClick={handleReset} variant="outlined">
                {t('general.reset')}
              </Button>
            </Box>
          </form>
        </Paper>

        {device && (
          <Paper sx={{p: 3, mb: 2}}>
            <Typography variant="h5" sx={{mb: 2}}>
              {t('admin.devices.deviceFound')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.deviceIdLabel')}
                </Typography>
                <Typography variant="body1">{device.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.nameLabel')}
                </Typography>
                <Typography variant="body1">
                  {device.name || t('general.notAvailable')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.typeLabel')}
                </Typography>
                <Typography variant="body1">
                  {device.type || t('general.notAvailable')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.identifierLabel')}
                </Typography>
                <Typography variant="body1">
                  {device.identifier || t('general.notAvailable')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.qrCodeLabel')}
                </Typography>
                <Typography variant="body1" sx={{fontFamily: 'monospace'}}>
                  {device.qrCode || t('general.notAvailable')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.appFwVersionLabel')}
                </Typography>
                <Typography variant="body1">
                  {device.appFwVersion || t('general.notAvailable')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.is12MonthMembershipLabel')}
                </Typography>
                <Typography variant="body1">
                  {device.is12MonthMembership
                    ? t('general.yes')
                    : t('general.no')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.devices.hasStartedShopifySubscriptionLabel')}
                </Typography>
                <Typography variant="body1">
                  {device.hasStartedShopifySubscription
                    ? t('general.yes')
                    : t('general.no')}
                </Typography>
              </Grid>
              {device.profile && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{mt: 2, mb: 1}}>
                    {t('admin.devices.associatedProfile')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.devices.profileName')}
                      </Typography>
                      <Typography variant="body1">
                        {device.profile.firstName} {device.profile.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.devices.profileEmail')}
                      </Typography>
                      <Typography variant="body1">
                        {device.profile.email}
                      </Typography>
                    </Grid>
                  </Grid>
                  {device.profile.players &&
                    device.profile.players.length > 0 && (
                      <Box sx={{mt: 3}}>
                        <Typography variant="h6" sx={{mb: 2}}>
                          {t('admin.devices.associatedPlayers')}
                        </Typography>
                        <Grid container spacing={2}>
                          {device.profile.players.map((player, index) => (
                            <Grid item xs={12} key={player.id || index}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary">
                                    {t('admin.devices.playerId')}
                                  </Typography>
                                  <Typography variant="body1">
                                    {player.id || t('general.notAvailable')}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary">
                                    {t('admin.devices.playerNumber')}
                                  </Typography>
                                  <Typography variant="body1">
                                    {player.number || t('general.notAvailable')}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary">
                                    {t('admin.devices.playerActive')}
                                  </Typography>
                                  <Typography variant="body1">
                                    {player.isActive
                                      ? t('general.yes')
                                      : t('general.no')}
                                  </Typography>
                                </Grid>
                                {player.team && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary">
                                      {t('admin.devices.playerTeam')}
                                    </Typography>
                                    <Typography variant="body1">
                                      {player.team.name ||
                                        t('general.notAvailable')}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                </Grid>
              )}
            </Grid>

            {/* Unpair Device Button - only show if device has an associated profile */}
            {device.profile && (
              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}>
                <Typography variant="h6" sx={{mb: 2}}>
                  {t('admin.devices.deviceManagement')}
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleUnpairDevice(false)}
                  disabled={unpairing}
                  sx={{mr: 2}}>
                  {unpairing
                    ? t('admin.devices.unpairing')
                    : t('admin.devices.unpairDevice')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleUnpairDevice(true)}
                  disabled={unpairing}
                  sx={{mr: 2}}>
                  {unpairing
                    ? t('admin.devices.unpairing')
                    : t('admin.devices.unpairDeviceAndResetTrial')}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                  {t('admin.devices.unpairInstructions')}
                </Typography>
              </Box>
            )}

            {/* Pair Device Button - only show if device has no associated profile */}
            {!device.profile && device.id && (
              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}>
                <Typography variant="h6" sx={{mb: 2}}>
                  {t('admin.devices.deviceManagement')}
                </Typography>
                <PairDeviceModal deviceId={device.id} onPaired={handlePaired} />
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                  {t('admin.devices.pairInstructions')}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {unpairSuccess && (
          <Alert severity="success" sx={{mb: 2}}>
            {unpairSuccess}
          </Alert>
        )}

        {pairSuccess && (
          <Alert severity="success" sx={{mb: 2}}>
            {pairSuccess}
          </Alert>
        )}
      </Box>
    </>
  );
};

export default React.memo(AdminDevices);
